import { CUSTOM_ELEMENTS_SCHEMA, Component, OnInit } from '@angular/core';
import { CommonModule, NgClass, formatDate } from '@angular/common';
import { FormGroup, FormBuilder, Validators, FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';

import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AuthService } from '../../../services/auth.service';
import { ReportService } from '../../../services/report.service';
import { Utilities } from '../../../services/utilities';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SubmissionType } from '../../../models/enums';
import { AccountService } from '../../../services/account.service';
import { ODPProjectWorkflowViewModel } from '../../../models/odpprojectworkflow/odpProjectWorkflow.model';


@Component({
  selector: 'app-report-weekly-coder-consensus',
  templateUrl: './report-weekly-coder-consensus.component.html',
  styleUrls: ['./report-weekly-coder-consensus.component.css'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    FormsModule,
    NgClass, 
    MatButtonModule,
    MatInputModule, 
    MatDatepickerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatNativeDateModule, 
    ReactiveFormsModule
  ]
})
export class ReportWeeklyCoderConsensusComponent implements OnInit {
  coderForm: FormGroup;
  activeProjectTitle: string;
  activeProject: ODPProjectWorkflowViewModel;

  reportTypes: SubmissionTypeOption[] = [{
      description: "No Coding Results",
      submissionType: -1,
      fileDescription: "Abstracts List"
    }, 
    {
      description: "QC Comparison with ML (W3)",
      submissionType: SubmissionType.CoderMlComparison,
      fileDescription: "W3 Coding Results"
    }, 
    {
      description: "QC Coder Consensus (W2)",
      submissionType: SubmissionType.CoderConsensus,
      fileDescription: "W2 Coding Results"
    } 
  ]

  getFilename = (startDate: string, endDate: string, phase: string) =>
    `QC Completed ${phase} ${startDate} to ${endDate} (${this.activeProjectTitle}) ${this.reportService.FormattedDateNoSeconds}.xlsx`

  constructor(
    private accountService: AccountService,
    private alertService: AlertService,
    private authService: AuthService,
    private reportService: ReportService,
    private formBuilder: FormBuilder,
    private router: Router
  ) {
    this.activeProject = this.accountService.GetActiveProject();
    this.activeProjectTitle = this.activeProject.projectTitle;
    //this.activeProjectTitle = this.accountService.GetActiveProject().projectTitle;
   }

  endDateFilter = (d: Date): boolean => d >= this.coderForm.get('startDate').value;

  ngOnInit() {
    if (this.authService.isSessionExpired) { return this.router.navigateByUrl('/account/login'); }
    this.initForm();
  }

  initForm() {
    this.coderForm = this.formBuilder.group({
      startDate: [, Validators.required],
      endDate: [, Validators.required],
      phase: [, Validators.required]
    });
  }

  onSubmit() {
    if (this.coderForm.invalid) {
      return this.alertService.showMessage(
        'Unable to submit.', 'Make sure all fields are filled and there are no errors shown.', MessageSeverity.error);
    }

    if (this.coderForm.get('phase').value.submissionType == -1){
      this.reportNoCodingResults();
    } else {
      this.reportWithCodingResults();
    }  
  }

  reportWithCodingResults(){
    const startDate = formatDate(this.coderForm.get('startDate').value, 'MM-dd-yyyy', 'en-US');
    const endDate = formatDate(this.coderForm.get('endDate').value, 'MM-dd-yyyy', 'en-US');
    const subType = this.coderForm.get('phase').value.submissionType;

    const odpProjectWorkflowId = this.activeProject.odpProjectWorkflowId;

    this.alertService.startLoadingMessage();
    this.reportService.getWeeklyCoderConsensusReport(odpProjectWorkflowId, startDate, endDate, subType).subscribe({
      next: (data: ArrayBuffer) => {
        this.alertService.stopLoadingMessage();

        if (data) {
          const blob = new Blob([data], { type: this.reportService.ReportContentType });

          saveAs(blob, this.getFilename(
            startDate,
            endDate,
            this.coderForm.get('phase').value.fileDescription));
        } else {
          this.alertService.showStickyMessage(
            'Empty Result',
            `Weekly coder report returned an empty result for ${startDate} - ${endDate}.`,
            MessageSeverity.warn);
        }
      },
      error: (error) => {
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(
          'Server error',
          `An error ocurred while building the kappa report.\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`,
          MessageSeverity.error,
          error);
      }
    });
  }

  reportNoCodingResults() {
    const startDate = formatDate(this.coderForm.get('startDate').value, 'MM-dd-yyyy', 'en-US');
    const endDate = formatDate(this.coderForm.get('endDate').value, 'MM-dd-yyyy', 'en-US');
    const odpProjectWorkflowId = this.activeProject.odpProjectWorkflowId;

    this.alertService.startLoadingMessage();
    this.reportService.getWeeklyCoderReport(odpProjectWorkflowId, startDate, endDate).subscribe({
      next: (data: ArrayBuffer) => {
        this.alertService.stopLoadingMessage();

        if (data) {
          const blob = new Blob([data], { type: this.reportService.ReportContentType });
          saveAs(blob, 
            this.getFilename(startDate, 
            endDate, 
            this.coderForm.get('phase').value.fileDescription));
        } else {
          this.alertService.showStickyMessage(
            'Empty Result',
            `Weekly coder report returned an empty result for ${startDate} - ${endDate}.`,
            MessageSeverity.warn);
        }
      },
      error: (error) => {
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(
          'Server error',
          `An error ocurred while building the kappa report.\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`,
          MessageSeverity.error,
          error);
      }
    });
  }
}

interface SubmissionTypeOption {
  description: string;
  submissionType: SubmissionType | number;
  fileDescription: string;
}
